import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Plus, Search, FileText, Calendar, Send, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";

const statusColors = {
  draft: "bg-slate-100 text-slate-800",
  sent: "bg-blue-100 text-blue-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800"
};

const statusLabels = {
  draft: "Utkast",
  sent: "Sendt",
  paid: "Betalt",
  overdue: "Forfalt",
  cancelled: "Kansellert"
};

export default function Invoices() {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => base44.entities.Invoice.list('-invoice_date'),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => base44.entities.Invoice.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const filteredInvoices = invoices.filter(invoice =>
    invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const paidAmount = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const pendingAmount = filteredInvoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue').reduce((sum, inv) => sum + (inv.total_amount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Fakturaer</h1>
            <p className="text-slate-600 mt-1">Administrer fakturaer og fakturagrunnlag</p>
          </div>
          <Link to={createPageUrl("CreateInvoice")}>
            <Button className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Opprett Faktura
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Fakturert</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">
                {totalAmount.toLocaleString('nb-NO')} kr
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Betalt</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {paidAmount.toLocaleString('nb-NO')} kr
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Utestående</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {pendingAmount.toLocaleString('nb-NO')} kr
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Søk etter kunde eller fakturanummer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-slate-500">Laster fakturaer...</div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Ingen fakturaer funnet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-slate-900">
                            {invoice.customer_name}
                          </h3>
                          <Badge className={statusColors[invoice.status]}>
                            {statusLabels[invoice.status]}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {invoice.invoice_number || "Mangler nummer"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(invoice.invoice_date), 'dd MMM yyyy', { locale: nb })}
                          </span>
                          {invoice.total_hours && (
                            <span className="flex items-center gap-1">
                              {invoice.total_hours.toFixed(1)} timer
                            </span>
                          )}
                          {invoice.effective_rate && (
                            <span className="text-green-600">
                              Eff. sats: {invoice.effective_rate.toLocaleString('nb-NO')} kr/t
                            </span>
                          )}
                        </div>

                        {invoice.project_name && (
                          <p className="text-sm text-slate-500">
                            Prosjekt: {invoice.project_name}
                          </p>
                        )}
                      </div>

                      <div className="text-right space-y-2">
                        <div className="text-2xl font-bold text-slate-900">
                          {invoice.total_amount.toLocaleString('nb-NO')} kr
                        </div>
                        {invoice.due_date && invoice.status !== 'paid' && (
                          <p className="text-xs text-slate-500">
                            Forfall: {format(new Date(invoice.due_date), 'dd MMM yyyy', { locale: nb })}
                          </p>
                        )}
                        <div className="flex gap-2 justify-end">
                          {invoice.status === 'draft' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateStatusMutation.mutate({ id: invoice.id, status: 'sent' })}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Merk som sendt
                            </Button>
                          )}
                          {(invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue') && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => updateStatusMutation.mutate({ id: invoice.id, status: 'paid' })}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Merk som betalt
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}